import { useEffect, useState } from "react";
import styles from "./add.module.css";
import { TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { authData, withAdminAuth } from "../../components/GetAuth";
import Loader from "../../components/Loader";
import { Home } from "react-feather";
import { Editor } from "@tinymce/tinymce-react";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

const EDITOR_TOOLBAR = [
  "undo redo | bold italic underline strikethrough | forecolor backcolor removeformat | fontselect fontsize | alignleft aligncenter alignright alignjustify | numlist bullist | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | code",
  "bookind_id compnaylogo customer_name company_name company_phone company_email company_address company_url login_url track_booking_url",
];

const AddEmailTemplate = () => {
  const auth = authData();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [loading, setLoading] = useState(false);
  const [editorHeight, setEditorHeight] = useState(700);

  const { websiteData } = useWebsite();
  useSiteBranding(templateId ? "Edit email template" : "Add email template", websiteData);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      title: "",
      content: "",
      order: 0,
    },
  });

  useEffect(() => {
    const updateEditorHeight = () => {
      setEditorHeight(window.innerWidth <= 720 ? 420 : 700);
    };
    updateEditorHeight();
    window.addEventListener("resize", updateEditorHeight);
    return () => window.removeEventListener("resize", updateEditorHeight);
  }, []);

  useEffect(() => {
    if (templateId) {
      axios
        .get(process.env.REACT_APP_API_URL + "v1/email-templates/" + templateId, {
          headers: {
            Authorization: `Bearer ${auth?.api_token}`,
          },
        })
        .then((response) => {
          if (response.data.status === "SUCCESS") {
            let templateData = response.data.data;
            setValue("title", templateData?.title);
            setValue("content", templateData?.content);
            setValue("order", templateData?.order ?? 0);
          }
        });
    }
  }, [templateId, setValue, auth?.api_token]);

  const onSubmit = (data) => {
    setLoading(true);

    const request = templateId
      ? axios.post(process.env.REACT_APP_API_URL + "v1/email-templates/" + templateId, data, {
        headers: {
          Authorization: `Bearer ${auth?.api_token}`,
        },
      })
      : axios.post(process.env.REACT_APP_API_URL + "v1/email-templates", data, {
        headers: {
          Authorization: `Bearer ${auth?.api_token}`,
        },
      });

    return request
      .then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          navigate("/email-template/list");
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  return (
    <>
      {loading && <Loader />}
      <div className={styles.MainDiv}>
        <div className={styles.MainCard}>
          <div className={styles.PageTitleWrap}>
            <p className={styles.PageTitle}>{templateId ? "Edit Email Template" : "Add Email Template"}</p>
            <nav aria-label="breadcrumb">
              <ol className={styles.breadcrumb}>
                <li className={styles.breadcrumbItem}>
                  <span>
                    <Home />
                  </span>
                  Email Templates
                </li>
                <li className={`${styles.breadcrumbItem} ${styles.active}`}>{templateId ? "Edit" : "Add"}</li>
              </ol>
            </nav>
          </div>

          <form className={styles.Form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.FormRow}>
              <div className={styles.FormGroup}>
                <Controller
                  control={control}
                  {...register("title", { required: "This field is required" })}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className={styles.TitleField}
                      label="Title*"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
                {errors.title && <span className={styles.ErrorText}>{errors?.title?.message}</span>}
              </div>
              {/* Only platform templates set the order; company copies follow the main template */}
              {auth?.user_type === 1 && <div className={styles.FormGroup}>
                <Controller
                  control={control}
                  {...register("order", { min: { value: 0, message: "Order can't be negative" } })}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Order"
                      variant="outlined"
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                      helperText="Lower numbers are listed first"
                    />
                  )}
                />
                {errors.order && <span className={styles.ErrorText}>{errors?.order?.message}</span>}
              </div>}
            </div>

            <div className={styles.FormRow}>
              <div className={styles.FormGroupFull}>
                <label className={styles.FieldLabel}>
                  Email Content<span className={styles.Required}>*</span>
                </label>
                <Controller
                  name="content"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <div className={styles.EditorWrap}>
                      <Editor
                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                        value={field.value}
                        onEditorChange={(content) => field.onChange(content)}
                        init={{
                          height: editorHeight,
                          menubar: false,
                          plugins: ["lists", "code", "table"],
                          toolbar: EDITOR_TOOLBAR,
                          toolbar_mode: "wrap",
                          font_size_formats: "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
                          setup: (editor) => {
                            editor.ui.registry.addButton("compnaylogo", {
                              text: "Company Logo",
                              onAction: () =>
                                editor.insertContent(
                                  `<img src="https://dummyimage.com/260x80/cccccc/000000&text=Company+Logo" alt="" width="260" style="max-width:260px; width:100%; height:auto; display:block; margin:0 auto; border:0;" />`
                                ),
                            });
                            editor.ui.registry.addButton("bookind_id", {
                              text: "Booking Id",
                              onAction: () => editor.insertContent(`[Booking ID]`),
                            });
                            editor.ui.registry.addButton("customer_name", {
                              text: "Customer Name",
                              onAction: () => editor.insertContent(`[Name of Customer]`),
                            });
                            editor.ui.registry.addButton("company_name", {
                              text: "Company Name",
                              onAction: () => editor.insertContent(`[Name of Company]`),
                            });
                            editor.ui.registry.addButton("company_phone", {
                              text: "Company Phone",
                              onAction: () => editor.insertContent(`[Company Phone]`),
                            });
                            editor.ui.registry.addButton("company_email", {
                              text: "Company Email",
                              onAction: () => editor.insertContent(`[Company Email]`),
                            });
                            editor.ui.registry.addButton("company_address", {
                              text: "Company Address",
                              onAction: () => editor.insertContent(`[Company Address]`),
                            });
                            editor.ui.registry.addButton("company_url", {
                              text: "Company URL",
                              onAction: () => editor.insertContent(`[Company URL]`),
                            });
                            editor.ui.registry.addButton("login_url", {
                              text: "Login URL",
                              onAction: () => editor.insertContent(`[Login URL]`),
                            });
                            editor.ui.registry.addButton("track_booking_url", {
                              text: "Track Booking URL",
                              onAction: () => editor.insertContent(`[Track Booking URL]`),
                            });
                          },
                        }}
                      />
                    </div>
                  )}
                />
                {errors.content && <span className={styles.ErrorText}>{errors?.content?.message}</span>}
              </div>
            </div>

            <div className={styles.ActionsRow}>
              <button className={styles.SubmitButton} type="submit">
                {templateId ? "Update" : "Save"}
              </button>
              <button
                className={styles.CancelButton}
                type="button"
                onClick={(e) => navigate("/email-template/list")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default withAdminAuth(AddEmailTemplate);